import { z } from 'zod';
import type { translations } from '@/translations/translations';

/** Backend spec 003 — `PATCH /validation/:audioId/text`. */

export const AUDIO_TEXT_MAX_LENGTH = 1000;
const CORRECTOR_ROLES: ReadonlySet<string> = new Set(['moderator', 'admin', 'super_admin']);

const textSchema = z.string().trim().min(1).max(AUDIO_TEXT_MAX_LENGTH);

export const audioTextCorrectionRequestSchema = z
  .object({ phraseLocal: textSchema.optional(), phraseFr: textSchema.optional() })
  .strict()
  .refine((body) => body.phraseLocal !== undefined || body.phraseFr !== undefined);

export const audioTextCorrectionResultSchema = z
  .object({
    id: z.string().regex(/^[a-f0-9]{24}$/),
    phraseLocal: z.string().max(AUDIO_TEXT_MAX_LENGTH).nullable(),
    phraseFr: z.string().max(AUDIO_TEXT_MAX_LENGTH).nullable(),
    changed: z.array(z.enum(['phraseLocal', 'phraseFr'])).max(2),
    textCorrectedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();

export type AudioTexts = { phraseLocal: string; phraseFr: string };
export type AudioTextCorrectionRequest = z.infer<typeof audioTextCorrectionRequestSchema>;
export type AudioTextCorrectionResult = z.infer<typeof audioTextCorrectionResultSchema>;
export type AudioTextCorrectionCopy = (typeof translations)['fr']['audioTextCorrection'];
export type AudioTextCorrectionErrorKey = 'invalid' | 'forbidden' | 'notFound' | 'changed' | 'failed';

export const canCorrectAudioText = (role: string | undefined) => role !== undefined && CORRECTOR_ROLES.has(role);

/** Sends only the texts the moderator actually changed; null when nothing changed. */
export function buildCorrectionRequest(current: AudioTexts, draft: AudioTexts): AudioTextCorrectionRequest | null {
  const request: AudioTextCorrectionRequest = {};
  if (draft.phraseLocal.trim() !== current.phraseLocal.trim()) request.phraseLocal = draft.phraseLocal.trim();
  if (draft.phraseFr.trim() !== current.phraseFr.trim()) request.phraseFr = draft.phraseFr.trim();
  return request.phraseLocal === undefined && request.phraseFr === undefined ? null : request;
}

/** HTTP status → translation key, per the outcome table of spec 003 §2. */
export function audioTextCorrectionErrorKey(httpStatus: number | undefined): AudioTextCorrectionErrorKey {
  if (httpStatus === 400) return 'invalid';
  if (httpStatus === 403) return 'forbidden';
  if (httpStatus === 404) return 'notFound';
  if (httpStatus === 409) return 'changed';
  return 'failed';
}
